import { Component, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { delay, map, switchMap, take, tap } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { LoaderTheme } from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';
import { Profile, ProfileTier } from '@fcom/core-api/login';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import { ProfileService } from '@fcom/loyalty-profile/services/profile.service';
import { CmsTemplateService } from '@fcom/cms/services';
import { PageMetaService } from '@fcom/common/services';
import { CmsTemplate } from '@fcom/core-api';
import { LoyaltyRoutesPath, setPageMetadata } from '@fcom/loyalty-core';
import { finShare } from '@fcom/rx';
import { ExpandComponent } from '@fcom/common';

enum Status {
  INITIAL,
  LOADING,
  COMPLETE,
}

export enum PlusProfileQueryParams {
  Section = 'section',
  TwoFactorAuthEnabledBackButtonUrl = 'twoFactorAuthEnabledBackButtonUrl',
}

export enum PlusProfileQueryParamSectionValue {
  TwoFactorAuth = 'two-factor-auth',
  MailingAddress = 'mailing-address',
  MarketingPreferences = 'marketing-preferences',
  TravelProfileAndInterests = 'travel-profile-and-interests',
}

export type RouterQueryParams = {
  [PlusProfileQueryParams.Section]?: PlusProfileQueryParamSectionValue;
  [PlusProfileQueryParams.TwoFactorAuthEnabledBackButtonUrl]?: string;
};

@Component({
  templateUrl: 'plus-profile.component.html',
})
export class PlusProfileComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly ProfileTier = ProfileTier;
  readonly Status = Status;
  readonly LoaderTheme = LoaderTheme;
  readonly PlusProfileQueryParams = PlusProfileQueryParams;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  routerParams$: Observable<Params>;
  profile$: Observable<Profile>;
  template$: Observable<CmsTemplate>;
  pageTitle$: Observable<string>;

  status: Status = Status.INITIAL;

  @ViewChild('expandTwoFactorAuth') expandTwoFactorAuth: ExpandComponent;
  @ViewChild('expandTwoFactorAuthTrigger') expandTwoFactorAuthTrigger: ElementRef;

  @ViewChild('expandMailingAddress') expandMailingAddress: ExpandComponent;
  @ViewChild('expandMailingAddressTrigger')
  expandMailingAddressTrigger: ElementRef;

  @ViewChild('expandMarketingPreferences') expandMarketingPreferences: ExpandComponent;
  @ViewChild('expandMarketingPreferencesTrigger')
  expandMarketingPreferencesTrigger: ElementRef;

  @ViewChild('expandTravelProfile') expandTravelProfile: ExpandComponent;
  @ViewChild('expandTravelProfileTrigger')
  expandTravelProfileTrigger: ElementRef;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private router: ActivatedRoute,
    private scrollService: ScrollService,
    private profileService: ProfileService,
    private languageService: LanguageService,
    private cmsTemplateService: CmsTemplateService,
    private pageMetaService: PageMetaService
  ) {}

  ngOnInit(): void {
    this.status = Status.LOADING;

    this.routerParams$ = this.router.queryParams.pipe(take(1), finShare());
    this.profile$ = this.profileService.fetchProfile().pipe(finShare());

    this.subscriptions.add(
      this.profile$
        .pipe(
          take(1),
          tap(() => {
            this.status = Status.COMPLETE;
          })
        )
        .subscribe()
    );

    this.template$ = this.cmsTemplateService.load(
      `/${this.languageService.langValue}/${LoyaltyRoutesPath.PLUS}/${LoyaltyRoutesPath.PROFILE}`
    );

    this.pageTitle$ = this.template$.pipe(map((template: CmsTemplate) => template?.header?.[0]?.title ?? ''));

    this.subscriptions.add(
      this.template$.subscribe((template: CmsTemplate) => setPageMetadata(template, this.pageMetaService))
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.profile$
        .pipe(
          delay(0),
          switchMap(() => this.routerParams$),
          switchMap((params: RouterQueryParams) => {
            const triggerMap: {
              [key in PlusProfileQueryParamSectionValue]?: { expand: ExpandComponent; trigger: ElementRef };
            } = {
              [PlusProfileQueryParamSectionValue.MailingAddress]: {
                expand: this.expandMailingAddress,
                trigger: this.expandMailingAddressTrigger,
              },
              [PlusProfileQueryParamSectionValue.MarketingPreferences]: {
                expand: this.expandMarketingPreferences,
                trigger: this.expandMarketingPreferencesTrigger,
              },
              [PlusProfileQueryParamSectionValue.TravelProfileAndInterests]: {
                expand: this.expandTravelProfile,
                trigger: this.expandTravelProfileTrigger,
              },
              [PlusProfileQueryParamSectionValue.TwoFactorAuth]: {
                expand: this.expandTwoFactorAuth,
                trigger: this.expandTwoFactorAuthTrigger,
              },
            };
            const expandComponentData = triggerMap[params.section];
            if (!expandComponentData) {
              return EMPTY;
            }
            this.scrollService.scrollToImmediate(expandComponentData.trigger);
            expandComponentData.trigger.nativeElement.focus();
            return expandComponentData.expand.isOpen$.pipe(
              take(1),
              map((isOpen) => [expandComponentData.expand, isOpen] as const)
            );
          }),
          tap(([expand, isOpen]) => {
            !isOpen && expand.toggleDetails();
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }
}
